import styled, { withTheme } from 'styled-components';
import { MdChevronRight } from 'react-icons/md';
import React, { Fragment } from 'react';

const StyledButton = styled.button`
  font-size: 15px;
  font-weight: 500;
  padding: 10px;
  border-radius: 4px;
  border: 1px;
  :focus {
    outline: 0;
  }
`;

export const PrimaryButton = withTheme(styled(StyledButton)`
  color: #fff;
  padding: ${(props) => props.roundedCorners && '14px'};
  border-radius: ${(props) => props.roundedCorners && '28px'};
  background: ${(props) => props.theme.colors.PRIMARY};
  padding: 10px 20px;
  position: relative;
  min-width: 160px;
  transition: background-color 300ms ease-out;
  span {
    display: inline-block;
    position: relative;
    transition: all 300ms ease-out;
    will-change: transform;
  }
  &:hover span {
    transform: translate3d(-4px, 0, 0);
  }
  svg {
    position: absolute;
    display: ${(props) => (props.loading ? 'none' : '')};
    font-size: 18px;
    right: -18px;
    opacity: 0;
    top: 50%;
    transform: translateY(-50%);
    transition: all 300ms ease-out;
    will-change: right, opacity;
  }
  &:hover svg {
    opacity: 1;
    right: -15px;
  }
  :active {
    background: ${(props) => props.theme.colors.PRIMARY_DARK};
  }
  :disabled {
    cursor: not-allowed;
  }
  @media (max-width: 575px) {
    min-width: ${(props) => (props.isfromb2b ? '' : '120px')};
  }
  @media (min-width: 576px) {
    max-width: ${(props) => (props.isfromb2b ? '120px' : '')};
  }
`);

export const DarkPrimaryButton = withTheme(styled(StyledButton)`
  color: #fff;
  min-width: 140px;
  padding: 14px;
  background: transparent;
  border: 2px solid #ffffff80;
  :hover {
    border: 2px solid #ffffffed;
  }
  :disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  @media (max-width: 600px) {
    min-width: 120px;
  }
`);

export const LightPrimaryButton = withTheme(styled(StyledButton)`
  color: #fff;
  min-width: 160px;
  padding: 14px;
  background: #097db7;
  :hover {
    color: #fff;
    background: ${(props) => props.theme.colors.PRIMARY_LIGHT};
  }
  :active {
    background: ${(props) => props.theme.colors.PRIMARY_DARK};
  }
  :disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  @media (max-width: 600px) {
    min-width: 120px;
  }
`);

export const PrimaryWithoutActionButton = withTheme(styled(StyledButton)`
  color: #fff;
  min-width: 160px;
  padding: 14px;
  background: ${(props) => props.theme.colors.PRIMARY};
  :hover {
    color: #fff;
    background: ${(props) => props.theme.colors.PRIMARY_LIGHT};
  }
  :active {
    background: ${(props) => props.theme.colors.PRIMARY_LIGHT};
  }
  :disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  @media (max-width: 600px) {
    min-width: 120px;
  }
`);

export const SecondaryButton = withTheme(styled(StyledButton)`
  color: ${(props) => props.theme.colors.PRIMARY};
  background: #fff;
  min-width: 160px;
  border: 2px solid;
  padding: 14px;
  border-color: ${(props) => props.theme.colors.PRIMARY};
  :hover {
    background: var(--siteSecondaryBgColor);
  }
  :active {
    background: var(--siteBorderColor);
    opacity: 0.9;
  }
  :disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  @media (max-width: 600px) {
    min-width: 120px;
  }
`);

export const GradientButton = withTheme(styled(SecondaryButton)`
  padding: 13px 25px 13px 25px;
  color: #fff;
  min-width: initial;
  background: linear-gradient(to right, #374cb9 0%, #2d4ef5 100%);
  border: 1px;
  border-radius: 30px;
`);

export const TransparentButton = withTheme(styled(SecondaryButton)`
  color: #fff;
  background: Transparent;
  border: 2px solid #fff;
  width: 150px;
  border-radius: 2px;
`);

export const WhiteInActiveButton = withTheme(styled(StyledButton)`
  color: ${(props) => props.theme.colors.PRIMARY};
  background: #fff;
  min-width: 160px;
  border: 2px solid;
  padding: 14px;
  border-color: ${(props) => props.theme.colors.PRIMARY};
  cursor: initial;
  :hover {
    background: #fff;
  }
  :focus {
    outline: 0;
  }
`);

export const AnimatedButton = withTheme(styled(StyledButton)`
  color: ${(props) => props.theme.colors.PRIMARY};
  background: #fff;
  min-width: 160px;
  animation: beat 0.25s infinite alternate;
  padding: 14px;
  :hover {
    color: #fff;
    background: ${(props) => props.theme.colors.PRIMARY_LIGHT};
  }
  :active {
    background: ${(props) => props.theme.colors.PRIMARY_DARK};
  }
  :disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  :focus {
    outline: 0;
  }
  @keyframes beat {
    to {
      transform: scale(1.03);
    }
  }
`);

const Button = ({
  primary,
  primaryWithoutAction,
  loading,
  darkPrimary,
  lightPrimary,
  secondary,
  gradient,
  animated,
  transparent,
  roundedCorners,
  children,
  onClick,
  style,
  disabled,
  isfromb2b,
}) => (
  <Fragment>
    {primary && (
      <PrimaryButton isfromb2b={isfromb2b} loading={loading} disabled={disabled} onClick={onClick} roundedCorners={roundedCorners} style={style}>
        <span>
          {children}
          <MdChevronRight />
        </span>
      </PrimaryButton>
    )}
    {primaryWithoutAction && (
      <PrimaryWithoutActionButton disabled={disabled} onClick={onClick} style={style}>
        {children}
      </PrimaryWithoutActionButton>
    )}
    {darkPrimary && (
      <DarkPrimaryButton disabled={disabled} onClick={onClick} style={style}>
        {children}
      </DarkPrimaryButton>
    )}
    {lightPrimary && (
      <LightPrimaryButton disabled={disabled} onClick={onClick} style={style}>
        {children}
      </LightPrimaryButton>
    )}
    {secondary && (
      <SecondaryButton disabled={disabled} style={style} onClick={onClick}>
        {children}
      </SecondaryButton>
    )}
    {gradient && (
      <GradientButton disabled={disabled} onClick={onClick} style={style}>
        {children}
      </GradientButton>
    )}
    {transparent && (
      <TransparentButton onClick={onClick} style={style}>
        {children}
      </TransparentButton>
    )}
    {animated && (
      <AnimatedButton animated disabled={disabled} style={style} onClick={onClick}>
        {children}
      </AnimatedButton>
    )}
  </Fragment>
);

export default Button;
